import Bugsnag from '@bugsnag/js';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { HttpStatusCode, isAxiosError } from 'axios';

import { Button } from '@/common/components/button';
import { Card } from '@/common/components/card';
import { CircleXmarkIcon } from '@/icons/fa/duotone-solid';
import { BugIcon, ChevronDownIcon } from '@/icons/fa/regular';
import { ErrorContext } from '@/shared/errors';

type UnhandledErrorDisplayProps = {
  error: ErrorContext;
  resetErrorBoundary: () => void;
};

/**
 * Displays a user-friendly interface when an unhandled error occurs.
 */
export function UnhandledErrorDisplay({ error, resetErrorBoundary }: UnhandledErrorDisplayProps) {
  if (isAxiosError(error) && error.status === HttpStatusCode.Unauthorized) {
    window.location.href = '/login';
  }

  const resetApp = () => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.reload();
    Bugsnag.setUser(null, null, null);
  };

  return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="mx-auto max-w-md w-full">
        <Card role="alert" className="flex flex-col items-center justify-center">
          <CircleXmarkIcon className="size-16 text-red-500"/>
          <h1 className="text-xl font-semibold mt-4">{error.title}</h1>

          <p className="text-gray-600 text-center mt-2">
            {error.message}
          </p>

          <div className="mt-6 flex flex-col gap-3 w-full">
            <Button onClick={resetErrorBoundary}>Try Again</Button>
            <Button outline onClick={resetApp}>Reload Page</Button>
          </div>

          <div className="w-full border-t border-gray-200 mt-6 pt-4">
            <Disclosure>
              <DisclosureButton className="group flex items-center justify-between gap-2 text-left text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer w-full">
                <span className="flex items-center gap-2">
                  <BugIcon className="size-4"/>
                  Technical Details
                </span>
                <ChevronDownIcon className="size-4 group-data-open:rotate-180 transition-transform"/>
              </DisclosureButton>
              <DisclosurePanel>
                <div className="rounded-md bg-gray-100 p-4 mt-1 max-h-72 overflow-y-auto">
                  <p className="whitespace-pre-wrap font-mono text-sm text-gray-600">
                    {error.exception?.stack}
                  </p>
                </div>
              </DisclosurePanel>
            </Disclosure>
          </div>

          <p className="text-xs text-gray-500 mt-4">
            If the problem persists, please reach out to <a href="https://www.talquinelectric.com/about-us/contact-us/" className="text-primary-800 underline">member services.</a>
          </p>
        </Card>
      </div>
    </div>
  );
}
